import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Modal, Space, Spin, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import breakdownCategoryService from "../../services/breakdownCategoryService";
import CategoryPickerModal from "./CategoryPickerModal";
import TyrePositionPicker from "./TyrePositionPicker";
import "../../assets/styles/categoryFaults.css";

const getDisplayLabel = (item) => {
  const category = item.category || item.freeTextCat || "N/A";
  const subcategory = item.subcategory || item.freeTextSubCat || "N/A";
  const component = item.component || "";
  const cause = item.causeName || "";
  const parts = [category, subcategory, component].filter(Boolean).join(" | ");
  return cause ? `${parts} -> ${cause}` : parts;
};

const parseTyrePositions = (value) =>
  String(value || "")
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));

const FaultList = ({ emptyText, items, onDelete, renderSuffix }) => {
  if (!items.length) {
    return <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className="category-fault-list">
      {items.map((item) => (
        <div className="category-fault-row" key={item.id}>
          <Typography.Text className="category-fault-row__text">
            {getDisplayLabel(item)}
            {renderSuffix ? renderSuffix(item) : null}
          </Typography.Text>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(item)}
            size="small"
            type="text"
          />
        </div>
      ))}
    </div>
  );
};

const BreakdownCategoryPanels = ({ breakdownId, className = "" }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [tyreModalOpen, setTyreModalOpen] = useState(false);
  const [tyrePickerOpen, setTyrePickerOpen] = useState(false);
  const [selectedTyres, setSelectedTyres] = useState([]);

  const loadRows = useCallback(async () => {
    if (!breakdownId) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const data = await breakdownCategoryService.getBreakdownCategories(breakdownId);
      setRows(data);
    } catch (error) {
      console.error("Failed to load breakdown categories:", error);
      message.error("Failed to load category details");
    } finally {
      setLoading(false);
    }
  }, [breakdownId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const categoryRows = useMemo(
    () => rows.filter((item) => String(item.category || item.freeTextCat).toUpperCase() !== "TYRES"),
    [rows]
  );

  const tyreRows = useMemo(
    () => rows.filter((item) => String(item.category || item.freeTextCat).toUpperCase() === "TYRES"),
    [rows]
  );

  const handleDelete = (item) => {
    Modal.confirm({
      title: "Remove category fault",
      content: "Are you sure you want to remove this category fault?",
      okText: "Remove",
      okButtonProps: { danger: true, loading: deletingId === item.id },
      onOk: async () => {
        setDeletingId(item.id);
        try {
          await breakdownCategoryService.deleteBreakdownCategory(item.id);
          message.success("Category removed");
          await loadRows();
        } catch (error) {
          console.error("Failed to remove category:", error);
          message.error("Failed to remove category");
          throw error;
        } finally {
          setDeletingId("");
        }
      },
    });
  };

  const handleTyreSaved = async () => {
    setSelectedTyres([]);
    await loadRows();
  };

  const toggleTyre = (position) => {
    setSelectedTyres((previous) =>
      previous.includes(position)
        ? previous.filter((item) => item !== position)
        : [...previous, position].sort((left, right) => left - right)
    );
  };

  return (
    <div className={`category-fault-panels ${className}`}>
      <Card
        className="category-fault-card"
        title="Category Details"
        extra={(
          <Button
            icon={<PlusOutlined />}
            onClick={() => setCategoryModalOpen(true)}
            size="small"
            type="primary"
          >
            Add
          </Button>
        )}
      >
        <Spin spinning={loading}>
          <FaultList
            emptyText="No category faults added."
            items={categoryRows}
            onDelete={handleDelete}
          />
        </Spin>
      </Card>

      <Card
        className="category-fault-card"
        title="Tyres"
        extra={(
          <Button
            icon={<PlusOutlined />}
            onClick={() => setTyreModalOpen(true)}
            size="small"
            type="primary"
          >
            Add
          </Button>
        )}
      >
        <Spin spinning={loading}>
          <FaultList
            emptyText="No tyre faults added."
            items={tyreRows}
            onDelete={handleDelete}
            renderSuffix={(item) => {
              const positions = parseTyrePositions(item.tyrePosition);
              return positions.length ? ` | Position: ${positions.join(", ")}` : "";
            }}
          />
        </Spin>
      </Card>

      <CategoryPickerModal
        breakdownId={breakdownId}
        open={categoryModalOpen}
        onCancel={() => setCategoryModalOpen(false)}
        onSaved={loadRows}
      />

      <CategoryPickerModal
        breakdownId={breakdownId}
        open={tyreModalOpen}
        onCancel={() => {
          setTyreModalOpen(false);
          setSelectedTyres([]);
        }}
        onSaved={handleTyreSaved}
        tyresOnly
        requireTyreSelection
        selectedTyres={selectedTyres}
        onOpenTyrePicker={() => setTyrePickerOpen(true)}
      />

      <Modal
        title="Select Tyre Position"
        open={tyrePickerOpen}
        onCancel={() => setTyrePickerOpen(false)}
        footer={(
          <Space style={{ display: "flex", justifyContent: "end", width: "100%" }}>
            <Button onClick={() => setTyrePickerOpen(false)}>Cancel</Button>
            <Button
              disabled={!selectedTyres.length}
              onClick={() => setTyrePickerOpen(false)}
              type="primary"
            >
              Done
            </Button>
          </Space>
        )}
        width={720}
        destroyOnHidden
      >
        <TyrePositionPicker selectedTyres={selectedTyres} onToggleTyre={toggleTyre} />
      </Modal>
    </div>
  );
};

export default BreakdownCategoryPanels;
